import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import Decimal from "decimal.js";
import moment from "moment-timezone";
import { CellFormat, excelColumnToNumber, numberToExcelColumn } from "./common";
import { ServerError, ValidationError } from "../responseResults";

export type CellValue = ExcelJS.CellValue;
export type Workbook = ExcelJS.Workbook;
export type Worksheet = ExcelJS.Worksheet;
export { CellFormat, excelColumnToNumber, numberToExcelColumn };

interface ParseResult {
  success: boolean,
  data: { excelJsWorkbook: ExcelJS.Workbook, xlsxWorkbook: XLSX.WorkBook } | null,
  error: any,
}
export async function parseFromFile(file: File): Promise<ParseResult> {
  const workbook = new ExcelJS.Workbook();
  const bufferResult = await parseExcelFromBrowserFile(file);

  if (!bufferResult.success) {
    return { success: false, data: null, error: bufferResult.error };
  }

  const buffer = bufferResult.data;

  return {
    success: true,
    error: null,
    data: {
      excelJsWorkbook: await workbook.xlsx.load(buffer as any),
      xlsxWorkbook: XLSX.read(buffer as any),
    }
  };
}

// Define the border style
export const borderStyle = {
  style: 'thin',
  color: { argb: 'FF000000' } // Light black color
};

export const lightGreenFill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFE6FFE6' }  // Light green color
};

export const Fills = {
  lightGrey: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFAAAAAA' },
  },
  lightGreen: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE6FFE6' },
  },
  lightYellow: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFFFF9C4' },
  },
  darkGrey: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF3A3A3A' },
  },
  primaryRed: {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF861810' },
  },
} as const;

export async function workbookToBuffer(
  workbook: ExcelJS.Workbook,
): Promise<Buffer> {
  return workbook.xlsx.writeBuffer() as any;
}

export function parseFromBrowserFile(
  file: File,
): Promise<{ success: boolean; data: string; error: any }> {
  return new Promise((res) => {
    var reader = new FileReader();
    reader.readAsText(file, "UTF-8");
    reader.onload = function (event) {
      res({
        success: true,
        data: (event.target?.result as string) || "",
        error: null,
      });
    };
    reader.onerror = function (event) {
      res({ success: false, data: "", error: event });
    };
  });
}

function parseExcelFromBrowserFile(
  file: File,
): Promise<{ success: boolean; data?: ArrayBuffer; error: any }> {
  return new Promise((res) => {
    var reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = function (event) {
      res({ success: true, data: event.target?.result as any, error: null });
    };
    reader.onerror = function (event) {
      res({ success: false, data: undefined, error: event });
    };
  });
}

export function safeParseDecimal(originalItem: string | number | Decimal, meta?: any): Decimal {
  const item = typeof originalItem === "string" ? originalItem?.trim()?.replace(/\$|,|\(|\)/img, "") : originalItem;

  try {
    if (item == null) { return new Decimal(0); }
    const decimal = new Decimal(item);
    if (decimal.isNaN()) {
      return new Decimal(0);
    }

    return decimal;
  } catch (error: any) {
    return new Decimal(0);
  }
}

export function isValidDecimal(item: string | number | Decimal | undefined, meta?: any): boolean {
  try {
    if (item == null) { return false }
    const decimal = typeof item === "string" ? new Decimal(item?.replace(/,|\$|%/img, "")) : new Decimal(item);
    if (decimal.isNaN()) {
      return false;
    }

    return true;
  } catch (error: any) {
    return false;
  }
}

export function setColumnInfo(sheet: ExcelJS.Worksheet, column: string | number, format: CellFormat, width = 8) {
  sheet.getColumn(column).numFmt = format;
  sheet.getColumn(column).width = width;
}


export function boldCell(sheet: ExcelJS.Worksheet, row: number, column: string | number) {
  const cell = sheet.getRow(row).getCell(column);
  if (cell.font) {
    cell.style = JSON.parse(JSON.stringify(cell.style));
    cell.font.bold = true;
  } else {
    cell.style.font = {
      ...JSON.parse(JSON.stringify(cell.style)),
      bold: true,
    };
  }
}

export function boldCellWith(row: ExcelJS.Row, column: string | number) {
  const cell = row.getCell(column);
  if (cell.font) {
    cell.style = JSON.parse(JSON.stringify(cell.style));
    cell.font.bold = true;
  } else {
    cell.style.font = {
      ...JSON.parse(JSON.stringify(cell.style)),
      bold: true,
    };
  }
}

export async function addImage(imageBuffer: Buffer, { worksheet, extension, workbook, range }: { workbook: ExcelJS.Workbook, range: string; extension?: string; worksheet: any }) {
  try {
    const imageId = workbook.addImage({
      buffer: imageBuffer as any,
      extension: 'jpeg',
    });

    worksheet.addImage(imageId, range);
  } catch (error) {
    throw new ServerError("Failed to add image", {})
  }
}

// Function to merge border styles
export function mergeBorders(existingBorder: any, newBorder: any) {
  return {
    top: newBorder.top || existingBorder.top,
    left: newBorder.left || existingBorder.left,
    bottom: newBorder.bottom || existingBorder.bottom,
    right: newBorder.right || existingBorder.right
  };
}

// Apply borders while preserving existing ones
export function applyBorderToRange(worksheet: any, startCell: any, endCell: any, borderSides: any) {
  for (let col = startCell.col; col <= endCell.col; col++) {
    for (let row = startCell.row; row <= endCell.row; row++) {
      const cell = worksheet.getCell(row, col);
      const newBorder: any = {};
      for (const side of borderSides) {
        newBorder[side] = borderStyle;
      }
      cell.border = mergeBorders(cell.border || {}, newBorder);
    }
  }
}

// Convert column letters to numbers
export const colToNum = (col: any) => {
  let num = 0;
  for (let i = 0; i < col.length; i++) {
    num = num * 26 + (col.charCodeAt(i) - 64);
  }
  return num;
};

export function applyAroundBorders(worksheet: ExcelJS.Worksheet, startCell: string, endCell: string, borderStyle = {
  style: 'medium',
  color: { argb: '000000' }
}) {
  // Parse cell references to get row and column numbers
  const startMatch = startCell.match(/([A-Z]+)(\d+)/);
  const endMatch = endCell.match(/([A-Z]+)(\d+)/);

  if (!startMatch || !endMatch) {
    throw new Error('Invalid cell references');
  }

  const startCol = colToNum(startMatch[1]);
  const startRow = parseInt(startMatch[2]);
  const endCol = colToNum(endMatch[1]);
  const endRow = parseInt(endMatch[2]);

  // Iterate through the range
  for (let row = startRow; row <= endRow; row++) {
    for (let col = startCol; col <= endCol; col++) {
      const cell: any = worksheet.getCell(row, col);

      // Initialize empty border object
      cell.border = cell.border || {};

      // Apply left border if it's the first column
      if (col === startCol) {
        cell.border.left = borderStyle;
      }

      // Apply right border if it's the last column
      if (col === endCol) {
        cell.border.right = borderStyle;
      }

      // Apply top border if it's the first row
      if (row === startRow) {
        cell.border.top = borderStyle;
      }

      // Apply bottom border if it's the last row
      if (row === endRow) {
        cell.border.bottom = borderStyle;
      }
    }
  }
}


export function setFontColor(worksheet: any, cellRefs: string[], color: any) {
  const cells = getCells(worksheet, cellRefs);
  cells.forEach((cell: any) => {
    cell.font = cell.font || {};
    cell.font.color = { argb: color };
  });
}

export function setFontSize(worksheet: any, cellRefs: string[], size: any) {
  const cells = getCells(worksheet, cellRefs);
  cells.forEach((cell: any) => {
    cell.font = cell.font || {};
    cell.font.size = size;
  });
}

export function setCellFill(worksheet: any, cellRefs: string[], color: any) {
  const cells = getCells(worksheet, cellRefs);
  cells.forEach((cell: any) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: color }
    };
  });
}

export function setCellBorders(worksheet: any, cellRefs: string[], style = 'thin', color = '000000') {
  const cells = getCells(worksheet, cellRefs);
  const border = { style, color: { argb: color } };
  cells.forEach((cell: any) => {
    cell.border = {
      top: border,
      left: border,
      bottom: border,
      right: border
    };
  });
}

// Set cell fill
export function setCellAlignments(worksheet: any, cellRefs: string[], alignments = { vertical: 'middle', horizontal: 'center' }) {
  const cells = getCells(worksheet, cellRefs);
  cells.forEach((cell: any) => {
    cell.alignment = alignments;
  });
}

// Helper function to get cell objects from references
export function getCells(worksheet: any, cellRefs: string[]) {
  return cellRefs.map(ref => worksheet.getCell(ref));
}

// Helper function to get cell objects from references
export function getCell(worksheet: any, cellRef: string) {
  return worksheet.getCell(cellRef);
}


export function makePaperLikeSpreadsheet(worksheet: any) {
  // Hide gridlines
  worksheet.views = [
    { showGridLines: false }
  ];

  // Set white background for the entire sheet
  worksheet.background = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFFFFFFF' }
  };

  // Remove all borders from cells
  worksheet.eachRow((row: any) => {
    row.eachCell((cell: any) => {
      cell.border = {
        top: { style: 'none' },
        left: { style: 'none' },
        bottom: { style: 'none' },
        right: { style: 'none' }
      };
    });
  });

  // Optionally, set default font to a common typewriter or manuscript font
  worksheet.eachRow((row: any) => {
    row.font = {
      name: 'Calibri',
      size: 12
    };
  });

  // Set print area to fit content
  worksheet.pageSetup.fitToPage = true;
  worksheet.pageSetup.fitToWidth = 1;
  worksheet.pageSetup.fitToHeight = 0;
}

export function parseExcelDateToTimezone(item: Date, timezone: string) {
  return moment.tz(moment.utc(item.toISOString()).format("YYYY/MM/DD"), "YYYY/MM/DD", timezone);
}

export function genCellKey(column: string | number, row: number) {
  if (typeof column === 'string') return `${column}${row}`;

  return `${numberToExcelColumn(column)}${row}`;
}

export function getCellWith(sheet: any, column: string | number, row: number) {
  return sheet.getCell(genCellKey(column, row));
}

export function convertSheetToJson<T>(worksheet: Worksheet, colNumberOverrides?: Record<number, string>): T[] {
  const result: T[] = [];

  // Get the header row to map column letters to property names
  const headers: { [key: string]: string } = { ...(colNumberOverrides || {}) };
  worksheet.getRow(1).eachCell((cell, colNumber) => {
    if (colNumberOverrides && !!colNumberOverrides[colNumber]) {
      headers[colNumber] = colNumberOverrides[colNumber]?.trim();
    } else {
      headers[colNumber] = cell.value?.toString()?.trim() || '';
    }
  });

  // Iterate through data rows
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // Skip header row

    const rowData: any = {};
    row.eachCell((cell, colNumber) => {
      const header = headers[colNumber];
      if (header) {
        const formula = (cell.value as any)?.formula;
        const derivedFormulaDefault = /sum|countif|avg|average|count|if|sumif|max|min|vlookup|round/img.test(formula) ? 0 : '';

        rowData[header] = typeof cell.value === 'object' ? (cell.value as any)?.result || derivedFormulaDefault : cell.value?.toString() || '';
      }
    });

    result.push(rowData as T);
  });

  return result;
}

export function workbookSheetToJson<T>(worksheet: any, options: { withHeaders: boolean }): T[] {
    if (!options.withHeaders) { throw new ServerError("Must supply headers", {}); }
    const rows: any[] = [];

    // Get the header row
    const headerRow = worksheet.getRow(1) as any;
    const headers = headerRow.values.slice(1); // Remove the first empty cell

    // Iterate through rows, skipping the header
    worksheet.eachRow((row: any, rowNumber: number) => {
        if (rowNumber > 1) { // Skip the header row
            const rowObject: any = {};
            row.eachCell((cell: any, colNumber: number) => {
                const header = (headers[colNumber - 1] || "")?.trim();
                rowObject[header] = cell.value;
            });
            rows.push(rowObject);
        }
    });

    return rows;
}
